import React, { useEffect } from "react";

import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import posthog from "posthog-js";
import { hydrateRoot } from "react-dom/client";
import type { Locales } from "remix-utils/locales/server";

import { POSTHOG_API_KEY } from "./utils";
import { LocaleProvider } from "./utils/local-provider";

if (window.env?.SENTRY_DSN) {
  Sentry.init({
    dsn: window.env.SENTRY_DSN,
    environment: window.env.VERCEL_ENV || "development",
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Browser-side noise with no fix on our side. Each entry burned Sentry
    // quota as its own issue; keep this list to errors raised by the browser
    // or a third-party script, never by our code.
    ignoreErrors: [
      // WebKit reports a dropped connection, or a navigation that cancelled
      // an in-flight fetch, as a bare "Load failed".
      /^(TypeError: )?Load failed/,
      /entry\.worker\.js load failed/,
      // Safari tearing the page down while a promise is still pending.
      "Promise was rejected because the browsing context is going away",
      // Android WebView after the host app released its JavaScript bridge.
      "Error invoking postMessage: Java object is gone",
      // Microsoft Outlook SafeLinks / Office link scanners driving the page.
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
      // PostHog replay API called before its recorder script finished loading.
      "Called on script loaded before session recording is available",
    ],
    denyUrls: [
      // Vercel Toolbar's feedback widget, injected on preview deployments.
      /\/_next-live\/feedback\//,
    ],
    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      Sentry.replayIntegration(),
    ],
  });
}

const PosthogInit = () => {
  useEffect(() => {
    POSTHOG_API_KEY &&
      posthog.init(POSTHOG_API_KEY, {
        api_host: "https://us.i.posthog.com",
      });
  }, []);

  return null;
};

const locales = window.navigator.languages as Locales;

function getCookieValue(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : undefined;
}

const existingTimeZone = getCookieValue("timeZone");
const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timeZone = existingTimeZone || "UTC";

function removeUnexpectedHtmlChildren() {
  Array.from(document.documentElement.children).forEach((element) => {
    if (element.tagName !== "HEAD" && element.tagName !== "BODY") {
      element.remove();
    }
  });
}

function hydrate() {
  removeUnexpectedHtmlChildren();

  React.startTransition(() => {
    hydrateRoot(
      document,
      <React.StrictMode>
        <LocaleProvider locales={locales} timeZone={timeZone}>
          <RemixBrowser />
          <PosthogInit />
        </LocaleProvider>
      </React.StrictMode>
    );
  });
}

hydrate();

if (!existingTimeZone) {
  document.cookie = `timeZone=${browserTimeZone}; path=/; max-age=${
    60 * 60 * 24 * 365
  }; secure; samesite=lax`;
}
